import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

/**
 * AddressAutocomplete — powered by La Poste ControlAdresse V2 (backend-proxied).
 *
 * Props:
 *   value: { address, postal_code, city, country }
 *   onChange: (patch) => void  — receives {address?, postal_code?, city?, country?, insee_code?, address_check_status?}
 *   testidPrefix: string       — for data-testid stability
 *
 * Behaviour:
 *   - Free-form field on top; user types the full address.
 *   - Debounced 500 ms + min 5 chars; last query wins (previous is cancelled).
 *   - Backend cache handles duplicate queries; UI marks 'Adresse vérifiée' after user selects.
 *   - Original raw input is preserved separately (`address_raw`) via onChange.
 *   - If the API is down or unconfigured, the manual fields remain fully usable and the
 *     component surfaces a discreet, non-blocking notice.
 */
export default function AddressAutocomplete({ value = {}, onChange, testidPrefix = "address" }) {
  const [query, setQuery] = useState(value.address || "");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkedLabel, setCheckedLabel] = useState(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const listRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(-1);

  const emitPatch = useCallback((patch) => onChange && onChange(patch), [onChange]);

  useEffect(() => {
    // reset "checked" badge if user manually edits after selecting
    if (checkedLabel && query !== checkedLabel) setCheckedLabel(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const doSearch = useCallback(async (q) => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/integrations/laposte/address/suggest`, {
        params: { q },
        signal: ctrl.signal,
      });
      if (ctrl.signal.aborted) return;
      if (data.success) {
        setSuggestions(data.suggestions || []);
        setOpen((data.suggestions || []).length > 0);
      } else {
        setSuggestions([]);
        setOpen(false);
        if (data.error && !["query_too_short", "rate_limited_local"].includes(data.error)) {
          setError("La vérification automatique de l'adresse est temporairement indisponible. Vous pouvez continuer et notre équipe vérifiera les informations si nécessaire.");
        }
      }
    } catch (e) {
      if (e.name === "CanceledError" || e.name === "AbortError") return;
      setError("La vérification automatique de l'adresse est temporairement indisponible. Vous pouvez continuer.");
    } finally {
      setLoading(false);
    }
  }, []);

  const onQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    emitPatch({ address: v, address_check_status: "ADDRESS_NOT_CHECKED" });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v || v.trim().length < 5) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(v.trim()), 500);
  };

  const pickSuggestion = (s) => {
    setOpen(false);
    setSuggestions([]);
    const patch = {
      address: [s.street_number, s.street].filter(Boolean).join(" ").trim() || s.label || query,
      postal_code: s.postal_code || value.postal_code || "",
      city: s.city || value.city || "",
      country: s.country || value.country || "France",
      insee_code: s.insee_code || undefined,
      address_normalized: s.label,
      address_normalized_code: s.code,
      address_check_status: "ADDRESS_NORMALIZED",
      address_checked_at: new Date().toISOString(),
      address_provider: "laposte_control_adresse",
    };
    setQuery(patch.address);
    setCheckedLabel(patch.address);
    emitPatch(patch);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(suggestions.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pickSuggestion(suggestions[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const listId = useMemo(() => `${testidPrefix}-suggestions`, [testidPrefix]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid={`${testidPrefix}-block`}>
      <div className="md:col-span-4 relative">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${testidPrefix}-input`}>Adresse *</Label>
          {checkedLabel === query && (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 font-medium" data-testid={`${testidPrefix}-verified-badge`}>
              <ShieldCheck className="h-3 w-3" />Adresse vérifiée
            </span>
          )}
        </div>
        <div className="relative">
          <Input
            id={`${testidPrefix}-input`}
            data-testid={`${testidPrefix}-input`}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listId}
            value={query}
            onChange={onQueryChange}
            onKeyDown={onKeyDown}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onFocus={() => suggestions.length > 0 && setOpen(true)}
            placeholder="Ex. 1 rue de la Paix 75002 Paris"
            required
            autoComplete="street-address"
          />
          {loading && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
          {checkedLabel === query && !loading && <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-600" />}
        </div>
        {checkedLabel === query && (
          <p className="mt-1 text-xs text-muted-foreground">Adresse vérifiée avec les référentiels postaux de La Poste.</p>
        )}
        {error && (
          <p className="mt-1 text-xs text-amber-700 dark:text-amber-400 inline-flex items-center gap-1" data-testid={`${testidPrefix}-degraded`}>
            <AlertTriangle className="h-3 w-3" />{error}
          </p>
        )}
        {open && suggestions.length > 0 && (
          <ul
            id={listId}
            ref={listRef}
            role="listbox"
            data-testid={`${testidPrefix}-list`}
            className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-md max-h-64 overflow-auto"
          >
            {suggestions.map((s, idx) => (
              <li
                key={s.code || idx}
                role="option"
                aria-selected={activeIdx === idx}
                data-testid={`${testidPrefix}-option-${idx}`}
                onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`px-3 py-2 text-sm cursor-pointer ${activeIdx === idx ? "bg-secondary" : "hover:bg-secondary/60"}`}
              >
                <div className="font-medium">{s.label || `${s.street_number || ""} ${s.street || ""}`.trim()}</div>
                <div className="text-xs text-muted-foreground">{[s.postal_code, s.city].filter(Boolean).join(" ")}</div>
              </li>
            ))}
            <li
              className="px-3 py-2 text-xs text-muted-foreground border-t border-border cursor-pointer hover:bg-secondary/60"
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); emitPatch({ address_check_status: "ADDRESS_MANUALLY_CONFIRMED" }); }}
              data-testid={`${testidPrefix}-manual`}
            >
              Mon adresse n'apparaît pas — conserver ce que j'ai saisi
            </li>
          </ul>
        )}
      </div>
      <div>
        <Label>Code postal *</Label>
        <Input
          value={value.postal_code || ""}
          onChange={(e) => emitPatch({ postal_code: e.target.value, address_check_status: "ADDRESS_NOT_CHECKED" })}
          required
          data-testid={`${testidPrefix}-postal`}
          autoComplete="postal-code"
        />
      </div>
      <div className="md:col-span-2">
        <Label>Ville *</Label>
        <Input
          value={value.city || ""}
          onChange={(e) => emitPatch({ city: e.target.value, address_check_status: "ADDRESS_NOT_CHECKED" })}
          required
          data-testid={`${testidPrefix}-city`}
          autoComplete="address-level2"
        />
      </div>
      <div>
        <Label>Pays</Label>
        <Input
          value={value.country || "France"}
          onChange={(e) => emitPatch({ country: e.target.value })}
          data-testid={`${testidPrefix}-country`}
          autoComplete="country-name"
        />
      </div>
    </div>
  );
}
